import React, { useState, useEffect } from "react";
import { supabase } from "../../lib/supabase";
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Star, Clock, Users, MapPin, TrendingUp, Calendar, Check, X, 
  ChevronDown, ChevronUp, Phone, CreditCard, Wallet, Share2, Heart, AlertCircle,
  QrCode, Lock, Globe
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { companyInfo } from '../../data/mockData';
import { useApp } from '../../context/AppContext';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';

const processPayment = ({
  amount,
  bookingId,
  customerName,
  customerEmail,
  customerPhone,
  tourName,
}) => {
  const message = `
New Booking Request
Name: ${customerName}
Phone: ${customerPhone}
Email: ${customerEmail}
Tour: ${tourName}
Booking ID: ${bookingId}
Amount: ₹${amount}
`;

  const whatsapp = `https://wa.me/919816056934?text=${encodeURIComponent(message)}`;
  window.open(whatsapp, "_blank");
};

const TourDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tours, getOpenDates, addBooking } = useApp();
  const tour = tours.find(t => t.id === id);
  const [selectedMedia, setSelectedMedia] = useState(0);
  const [expandedDay, setExpandedDay] = useState(null);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [bookingStep, setBookingStep] = useState(1);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    guests: 1,
    paymentMethod: 'whatsapp'
  });
  const media = [
  ...tour.images.map(src => ({ type: 'image', src })),
  ...(tour.videos || []).map(src => ({ type: 'video', src }))
  ];
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    cardName: '',
    expiry: '',
    cvv: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);

useEffect(() => {
  const testSupabase = async () => {
    const { data, error } = await supabase
      .from("activitydates")
      .select("*")
      .limit(1);

    if (error) {
      console.error("❌ Supabase error:", error.message);
    } else {
      console.log("✅ Supabase connected. Sample data:", data);
    }
  };

  testSupabase();
}, []);

  useEffect(() => {
  if (!tour?.id) return;

  const fetchDates = async () => {
    const { data, error } = await supabase
      .from("activitydates") // MUST match your table name
      .select("*")
      .eq("tour_id", tour.id);

    if (error) {
      console.error("Error fetching dates:", error);
    } else {
      setBlockedDates(data || []);
    }
  };

  fetchDates();
},
 [tour?.id]);
 
 const isDateBlocked = (date) => {
  return blockedDates.some(d =>
    date >= new Date(d.start_date) &&
    date <= new Date(d.end_date)
  );
};

const addBlockedDate = async () => {
  await supabase.from("tour_dates").insert({
    tour_id: tour.id,
    start_date: selectedDate.startDate,
    end_date: selectedDate.endDate,
    status: "blocked",
  });
};


  if (!tour) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Tour not found</h1>
          <Link to="/" className="text-emerald-600 hover:underline">Go back home</Link>
        </div>
      </div>
    );
  }

  const openDates = getOpenDates(tour.id);
  const hasAvailableDates = openDates.length > 0;

  const handleOpenBooking = () => {
    if (!hasAvailableDates) return;
    setIsBookingOpen(true);
    setBookingStep(1);
    setSelectedDate(null);
    setBookingSuccess(false);
    setBookingForm({
      name: '',
      email: '',
      phone: '',
      guests: 1,
      paymentMethod: 'razorpay'
    });
    setCardDetails({ cardNumber: '', cardName: '', expiry: '', cvv: '' });
  };

  const handleDateSelect = (date) => setSelectedDate(date);

  const handleProceedToDetails = () => {
    if (!selectedDate) {
      alert('Please select a date to proceed');
      return;
    }
    setBookingStep(2);
  };

  const handleProceedToPayment = () => {
    if (!bookingForm.name || !bookingForm.phone) {
      alert('Please fill in all required fields');
      return;
    }
    setBookingStep(3);
  };

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length ? parts.join(' ') : value;
  };

  const formatExpiry = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  const sendWhatsAppBooking = () => {
  const totalAmount = tour.price * bookingForm.guests;

  const message = `
📩 *New Booking Request*

🏔 *Tour:* ${tour.name}
📅 *Dates:* ${formatDate(selectedDate.startDate)} - ${formatDate(selectedDate.endDate)}

👤 *Name:* ${bookingForm.name}
📞 *Phone:* ${bookingForm.phone}
📧 *Email:* ${bookingForm.email || 'Not provided'}
👥 *Guests:* ${bookingForm.guests}

💰 *Total Amount:* ₹${totalAmount}

Please confirm availability and payment details.
  `.trim();

  const whatsappNumber = companyInfo.whatsapp || '919816056934';

  window.open(
    `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`,
    '_blank'
  );
};

const handlePayment = () => {
  const totalAmount = tour.price * bookingForm.guests;

  // Save booking as pending
  addBooking({
    tourId: tour.id,
    tourName: tour.name,
    selectedDate: selectedDate,
    name: bookingForm.name,
    email: bookingForm.email,
    phone: bookingForm.phone,
    guests: bookingForm.guests,
    paymentMethod: 'whatsapp',
    totalAmount: totalAmount,
    paymentStatus: 'pending'
  });

  // Open WhatsApp
  sendWhatsAppBooking();

  setBookingSuccess(true);
};

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero */}
      <div className="pt-16">
        <div className="relative h-[50vh] md:h-[60vh]">
          {media[selectedMedia].type === 'image' ? (
  <img
    src={media[selectedMedia].src}
    alt={tour.name}
    className="w-full h-full object-contain"
  />
) : (
  <video
    src={media[selectedMedia].src}
    controls
    playsInline
    preload="metadata"
    poster={tour.coverImage}
    className="w-full h-full object-contain bg-black"
  />
)}

          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
          <button onClick={() => navigate(-1)} className="absolute top-24 left-4 md:left-8 p-2 bg-white/20 backdrop-blur-md rounded-full hover:bg-white/30 transition-colors">
            <ArrowLeft className="h-6 w-6 text-white" />
          </button>

          <div className="absolute top-24 right-4 md:right-8 flex gap-2">
            <button className="p-2 bg-white/20 backdrop-blur-md rounded-full hover:bg-white/30 transition-colors">
              <Share2 className="h-5 w-5 text-white" />
            </button>
            <button className="p-2 bg-white/20 backdrop-blur-md rounded-full hover:bg-white/30 transition-colors">
              <Heart className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        {/* Thumbnail Gallery */}
        <div className="max-w-7xl mx-auto px-4 -mt-16 relative z-10">
          <div className="flex gap-2 overflow-x-auto pb-4">
            {media.map((item, idx) => (
  <button
    key={idx}
    onClick={() => setSelectedMedia(idx)}
    className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${
      selectedMedia === idx
        ? 'border-emerald-500 scale-105'
        : 'border-white/50'
    }`}
  >
    {item.type === 'image' ? (
      <img src={item.src} alt="" className="w-full h-full object-cover" />
    ) : (
      <div className="w-full h-full bg-black flex items-center justify-center">
        <span className="text-white text-xl">▶</span>
      </div>
    )}
  </button>
))}

          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header */}
            <div>
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{tour.difficulty}</Badge>
                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">{tour.bestSeason}</Badge>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{tour.name}</h1>
              <div className="flex items-center gap-4 text-gray-600">
                <span className="flex items-center gap-1">
                  <Star className="h-5 w-5 text-amber-500 fill-amber-500" />
                  {tour.rating} ({tour.reviews} reviews)
                </span>
              </div>
            </div>

            {/* Quick Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: Clock, label: 'Duration', value: tour.duration },
                { icon: Users, label: 'Group Size', value: tour.groupSize },
                { icon: TrendingUp, label: 'Max Altitude', value: tour.altitude },
                { icon: MapPin, label: 'Start/End', value: `${tour.startPoint}/${tour.endPoint}` },
              ].map((item, idx) => (
                <div key={idx} className="bg-white rounded-2xl p-4 shadow-sm">
                  <item.icon className="h-6 w-6 text-emerald-600 mb-2" />
                  <p className="text-sm text-gray-500">{item.label}</p>
                  <p className="font-semibold text-gray-900 text-sm">{item.value}</p>
                </div>
              ))}
            </div>

            {/* Description */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-xl font-bold text-gray-900 mb-4">About This Tour</h2>
              <p className="text-gray-600 leading-relaxed">{tour.description}</p>
            </div>

            {/* Highlights */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Highlights</h2>
              <div className="grid md:grid-cols-2 gap-3">
                {tour.highlights.map((highlight, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Check className="h-4 w-4 text-emerald-600" />
                    </div>
                    <span className="text-gray-700 text-sm">{highlight}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Itinerary */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Day-by-Day Itinerary</h2>
              <div className="space-y-3">
                {tour.itinerary.map((day, idx) => (
                  <div key={idx} className="border border-gray-100 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setExpandedDay(expandedDay === idx ? null : idx)}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <span className="font-bold text-emerald-700 text-sm">{day.day}</span>
                        </div>
                        <div className="text-left">
                          <p className="font-semibold text-gray-900 text-sm">{day.title}</p>
                          <p className="text-xs text-gray-500">{day.distance}</p>
                        </div>
                      </div>
                      {expandedDay === idx ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                    </button>
                    {expandedDay === idx && (
                      <div className="px-4 pb-4 pt-0">
                        <div className="ml-14 p-4 bg-gray-50 rounded-xl">
                          <p className="text-gray-600 text-sm">{day.activities}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Inclusions / Exclusions */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Check className="h-5 w-5 text-emerald-600" /> Inclusions
                </h3>
                <ul className="space-y-2">
                  {tour.inclusions.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-600 text-sm">
                      <Check className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <X className="h-5 w-5 text-red-500" /> Exclusions
                </h3>
                <ul className="space-y-2">
                  {tour.exclusions.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-600 text-sm">
                      <X className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Sidebar - Booking */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-emerald-600" />
                    Available Dates
                  </h4>
                  {hasAvailableDates ? (
                    <div className="space-y-2">
                      {openDates.slice(0, 3).map((date) => (
                        <div key={date.id} className="p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                          <p className="font-medium text-emerald-800 text-sm">
                            {formatDate(date.startDate)} - {formatDate(date.endDate)}
                          </p>
                        </div>
                      ))}
                      {openDates.length > 3 && (
                        <p className="text-sm text-emerald-600 font-medium">+{openDates.length - 3} more dates</p>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-100 rounded-xl border border-gray-200">
                      <p className="text-gray-500 text-sm flex items-start gap-2">
                        <AlertCircle className="h-5 w-5 flex-shrink-0" />
                        No dates available. Contact us for custom dates.
                      </p>
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleOpenBooking}
                  disabled={!hasAvailableDates}
                  className={`w-full py-6 text-lg rounded-xl ${hasAvailableDates ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-gray-300 cursor-not-allowed'}`}
                >
                  {hasAvailableDates ? 'Select Date & Book' : 'No Dates Available'}
                </Button>

                <a href={`tel:${companyInfo.phone}`} className="flex items-center justify-center gap-2 mt-4 text-emerald-600 hover:underline text-sm">
                  <Phone className="h-4 w-4" />
                  Have Questions? Call Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      <Dialog open={isBookingOpen} onOpenChange={setIsBookingOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {bookingSuccess ? '🎉 Booking Confirmed!' : bookingStep === 1 ? 'Select Your Date' : bookingStep === 2 ? 'Your Details' : 'Complete Payment'}
            </DialogTitle>
            <DialogDescription>
              {bookingSuccess ? 'Your booking has been confirmed' : bookingStep === 1 ? 'Choose from available dates' : bookingStep === 2 ? 'Fill in your contact information' : 'Choose your payment method'}
            </DialogDescription>
          </DialogHeader>
          
          {bookingSuccess ? (
            <div className="py-6 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="h-10 w-10 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h3>
              <p className="text-gray-600 mb-4">Your booking has been confirmed. Our team will contact you shortly.</p>
              <div className="bg-gray-50 rounded-xl p-4 text-left mb-4">
                <p className="text-sm text-gray-500 mb-1">Booking Details:</p>
                <p className="font-medium">{tour.name}</p>
                {selectedDate && <p className="text-sm text-gray-600">{formatDate(selectedDate.startDate)} - {formatDate(selectedDate.endDate)}</p>}
                <p className="text-sm text-gray-600">{bookingForm.guests} Guest(s)</p>
                <p className="font-bold text-emerald-600 mt-2">Total Paid: ₹{(tour.price * bookingForm.guests).toLocaleString()}</p>
              </div>
              <Button onClick={() => setIsBookingOpen(false)} className="w-full bg-emerald-600 hover:bg-emerald-700">Done</Button>
            </div>
          ) : bookingStep === 1 ? (
            <div className="space-y-4 mt-4">
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {openDates.map((date) => (
                  <button
                    key={date.id}
                    onClick={() => handleDateSelect(date)}
                    className={`w-full p-4 rounded-xl text-left transition-all border-2 ${selectedDate?.id === date.id ? 'bg-emerald-50 border-emerald-500' : 'bg-gray-50 hover:bg-gray-100 border-transparent'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-gray-900">{formatDate(date.startDate)} - {formatDate(date.endDate)}</p>
                        <p className="text-sm text-gray-500 mt-1">{tour.duration}</p>
                      </div>
                      {selectedDate?.id === date.id && (
                        <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                          <Check className="h-4 w-4 text-white" />
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              <Button onClick={handleProceedToDetails} disabled={!selectedDate} className={`w-full py-6 text-lg rounded-xl ${selectedDate ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-gray-300 cursor-not-allowed'}`}>
                Continue with Selected Date
              </Button>
            </div>
          ) : bookingStep === 2 ? (
            <div className="space-y-4 mt-4">
              {selectedDate && (
                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                  <p className="text-sm text-emerald-700 font-medium">Selected Date:</p>
                  <p className="font-bold text-emerald-800">{formatDate(selectedDate.startDate)} - {formatDate(selectedDate.endDate)}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <input type="text" value={bookingForm.name} onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none" placeholder="Enter your name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                <input type="tel" value={bookingForm.phone} onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none" placeholder="Your phone number" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={bookingForm.email} onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none" placeholder="Your email (optional)" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Guests</label>
                <select value={bookingForm.guests} onChange={(e) => setBookingForm({ ...bookingForm, guests: parseInt(e.target.value) })} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (<option key={n} value={n}>{n} {n === 1 ? 'Guest' : 'Guests'}</option>))}
                </select>
              </div>
              <div className="flex gap-3">
                <Button onClick={() => setBookingStep(1)} variant="outline" className="flex-1 py-6 rounded-xl">Back</Button>
                <Button onClick={handleProceedToPayment} className="flex-1 bg-emerald-600 hover:bg-emerald-700 py-6 text-lg rounded-xl">Proceed to Payment</Button>
              </div>
            </div>
          ) : (
  // WhatsApp Booking Step
  <div className="space-y-4 mt-4">

    <div className="p-4 bg-gray-50 rounded-xl text-sm text-gray-700">
      <p className="font-semibold mb-2">What happens next?</p>
      <ul className="list-disc ml-5 space-y-1">
        <li>You’ll be redirected to WhatsApp</li>
        <li>We’ll confirm availability</li>
        <li>UPI / payment details will be shared</li>
      </ul>
    </div>

    <div className="flex gap-3">
      <Button
        onClick={() => setBookingStep(1)}
        variant="outline"
        className="flex-1 py-6 rounded-xl"
      >
        Back
      </Button>

      <Button
        onClick={() => {
          processPayment({
            method: "whatsapp",
            amount: tour.price * bookingForm.guests,
            bookingId: `BK_${Date.now()}`,
            customerName: bookingForm.name,
            customerEmail: bookingForm.email,
            customerPhone: bookingForm.phone,
            tourName: tour.title,
            onSuccess: () => {},
            onFailure: () => alert("Failed to open WhatsApp"),
          });
        }}
        className="flex-1 bg-emerald-600 hover:bg-emerald-700 py-6 text-lg rounded-xl"
      >
        Continue on WhatsApp
      </Button>
    </div>
  </div>
)}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default TourDetail;
